import React, { useCallback, useMemo, useEffect, type ComponentPropsWithoutRef } from 'react';
import { styled, css } from '@compiled/react';
import Heading from '@atlaskit/heading';
import IssuesIcon from '@atlaskit/icon/glyph/issues';
import QuestionCircleIcon from '@atlaskit/icon/glyph/question-circle';
import Lozenge from '@atlaskit/lozenge';
import { colors, fontSizeSmall } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { fireErrorAnalytics } from '@atlassian/jira-errors-handling';
import { useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import ErrorRetry from '@atlassian/jira-software-devops-report-common-error-retry';
import type { EChartsEvent, BatchSizeChartDataPoint } from '../../common/types';
import { TrendLozenge } from '../../common/ui/trend-lozenge';
import { IssuesTrendSummary } from '../../common/ui/trend-summary';
import { getWeekRangeFromWeekStartDate } from '../../common/utils/date-range';
import { useSelectedWeekStore } from '../../controllers/selected-date-range';
import { useSelectedDeploymentActions } from '../../controllers/selected-deployment';
import { useReportCoreData } from '../../services/deployment-frequency-core';
import { useViewDeploymentFrequencyReportExperienceFail } from '../../services/experience-tracker';
import { usePeriodBreakdownMetrics } from '../../services/period-breakdown';
import Chart from './chart';
import { convertToDataPoints } from './chart/utils';
import { LoadingSkeleton } from './loading-skeleton';
import messages from './messages';

const ChartTitle = () => {
	const { formatMessage } = useIntl();

	return (
		<WeekBreakDownTitle>
			{formatMessage(messages.weekBreakDownTitle)}
			<Tooltip content={formatMessage(messages.weekBreakDownTitleTooltip)} position="right">
				<QuestionIcon>
					<QuestionCircleIcon size="small" label="" />
				</QuestionIcon>
			</Tooltip>
		</WeekBreakDownTitle>
	);
};

export const BatchSizeChart = () => {
	const onViewDeploymentFrequencyReportExperienceFail =
		useViewDeploymentFrequencyReportExperienceFail();
	const { formatDate, formatMessage, formatNumber } = useIntl();
	const [{ selectedWeekStart }] = useSelectedWeekStore();
	const selectedDateRange = selectedWeekStart
		? getWeekRangeFromWeekStartDate(selectedWeekStart)
		: null;
	const { data: coreData, error: coreError, fetch: coreFetch } = useReportCoreData();
	const { loading, error, data, fetch } = usePeriodBreakdownMetrics(selectedDateRange);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [, { setSelectedDeploymentId }] = useSelectedDeploymentActions();
	const onChartClicked = useCallback(
		({ data: eventData }: EChartsEvent<BatchSizeChartDataPoint>) => {
			fireUIAnalytics(
				createAnalyticsEvent({
					action: 'clicked',
					actionSubject: 'batchSizeChartDeployment',
				}),
			);
			setSelectedDeploymentId(eventData.name);
		},
		[createAnalyticsEvent, setSelectedDeploymentId],
	);
	const events = useMemo(() => ({ click: onChartClicked }), [onChartClicked]);

	useEffect(() => {
		if (error && data == null) {
			onViewDeploymentFrequencyReportExperienceFail('BatchSizeChart', error);
			fireErrorAnalytics({
				meta: {
					id: 'BatchSizeChartDataFailed',
					packageName: 'jiraSoftwareDeploymentFrequencyReport',
					teamName: 'fusion-solaris',
				},
				error,
				sendToPrivacyUnsafeSplunk: true,
			});
		}
	}, [data, error, onViewDeploymentFrequencyReportExperienceFail]);

	if (error && data == null) {
		// if core data is failed, it doesn't make sense to retry on perDeploymentMetrics
		return (
			<>
				<ChartTitle />
				<ErrorUIWrapper data-test-id="software-deployment-frequency-report.ui.batch-size-chart.error-ui">
					<ErrorRetry onRetry={coreError && coreData == null ? coreFetch : fetch} />
				</ErrorUIWrapper>
			</>
		);
	}

	if (loading || data == null || selectedWeekStart == null || selectedDateRange === null) {
		return <LoadingSkeleton />;
	}

	const isEmptyState = data.perDeploymentMetrics.length === 0;
	const dataPoints = convertToDataPoints(data.perDeploymentMetrics);

	return (
		<BatchSizeChartContainer data-test-id="software-deployment-frequency-report.ui.batch-size-chart">
			<ChartTitle />
			<PeriodDate>
				{formatMessage(messages.weekTitle, {
					date: formatDate(selectedWeekStart, {
						month: 'short',
						day: 'numeric',
					}),
				})}
			</PeriodDate>
			<TitleContainer>
				<Title>
					{formatMessage(messages.issuesPerDeployment, {
						periodProdIssuesPerDeployment: formatNumber(data.periodProdIssuesPerDeployment ?? 0),
					})}
				</Title>
				{data.periodProdDeployments > 0 && (
					<TrendSummaryContainer>
						{data.prodDataState === 'SUFFICIENT_FOR_TREND' && (
							<TrendLozengeContainer>
								<TrendLozenge
									current={data.periodProdIssuesPerDeployment ?? 0}
									median={data.medianIssuesPerDeployment ?? 0}
									colorScheme="UP_IS_BAD"
								/>
							</TrendLozengeContainer>
						)}
						<IssuesTrendSummary
							weekCount={data.periodProdIssuesPerDeployment ?? 0}
							wholePeriodStatistic={data.medianIssuesPerDeployment ?? 0}
							prodDataState={data.prodDataState}
						/>
					</TrendSummaryContainer>
				)}
			</TitleContainer>
			<DescriptionAndLegendContainer>
				{isEmptyState ? (
					<Lozenge appearance="new">{formatMessage(messages.moreDataRequired)}</Lozenge>
				) : (
					<>
						<Description>
							<IssuesIcon size="small" label="" />
							<DescriptionText>
								{formatMessage(messages.weeklyProdDeployments, {
									weeklyProdDeployments: data.periodProdDeployments,
								})}
							</DescriptionText>
						</Description>
						<LegendContainer>
							<LegendEntity>
								<ChartSymbol />
								<LegendLabel>{formatMessage(messages.deployment)}</LegendLabel>
							</LegendEntity>
							<LegendEntity>
								<span>--</span>
								<LegendLabel>{formatMessage(messages.twelveWeekMedian)}</LegendLabel>
							</LegendEntity>
						</LegendContainer>
					</>
				)}
			</DescriptionAndLegendContainer>
			<GraphWrapper>
				<Chart
					dataPoints={dataPoints}
					median={data.medianIssuesPerDeployment}
					dateRange={selectedDateRange}
					onEvents={events}
				/>
			</GraphWrapper>
		</BatchSizeChartContainer>
	);
};

const graphBaseStyles = css({
	height: '272px',
});

const chartWrapperBaseStyles = css({
	padding: token('space.0', '0px'),
});

const Title = (props: ComponentPropsWithoutRef<typeof TitleComponent>) => (
	<Heading size="medium" as="h3">
		<TitleComponent {...props} />
	</Heading>
);

const PeriodDate = (props: ComponentPropsWithoutRef<typeof PeriodDateComponent>) => (
	<Heading level="h300" as="h3">
		<PeriodDateComponent {...props} />
	</Heading>
);

const WeekBreakDownTitle = (
	props: ComponentPropsWithoutRef<typeof WeekBreakDownTitleComponent>,
) => (
	<Heading size="xsmall" as="h3">
		<WeekBreakDownTitleComponent {...props} />
	</Heading>
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
const BatchSizeChartContainer = styled.div(chartWrapperBaseStyles);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TitleContainer = styled.div({
	display: 'flex',
	marginTop: token('space.050', '4px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Description = styled.span({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	fontSize: `${fontSizeSmall}px`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.text.subtle', colors.N400),
	display: 'flex',
	alignItems: 'center',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
const GraphWrapper = styled.div(graphBaseStyles);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const DescriptionAndLegendContainer = styled.div({
	display: 'flex',
	justifyContent: 'space-between',
	marginTop: token('space.100', '8px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const LegendContainer = styled.div({
	display: 'flex',
	alignItems: 'center',
	paddingRight: token('space.250', '20px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const LegendEntity = styled.div({
	display: 'flex',
	alignItems: 'center',
	paddingLeft: token('space.100', '8px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const LegendLabel = styled.span({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	fontSize: `${fontSizeSmall}px`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	lineHeight: `${fontSizeSmall}px`,
	marginLeft: token('space.050', '4px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TrendSummaryContainer = styled.div({
	display: 'flex',
	marginLeft: token('space.150', '12px'),
	alignItems: 'center',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
const ErrorUIWrapper = styled.div(graphBaseStyles, {
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const QuestionIcon = styled.div({
	marginLeft: token('space.050', '4px'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	fill: token('color.icon.inverse', colors.N800),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.icon', colors.N80),
	cursor: 'pointer',
	paddingTop: token('space.025', '2px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const DescriptionText = styled.span({
	marginLeft: token('space.050', '4px'),
	display: 'inline-flex',
	alignItems: 'center',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TrendLozengeContainer = styled.span({
	marginRight: token('space.050', '4px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ChartSymbol = styled.div({
	width: token('space.100', '8px'),
	height: token('space.100', '8px'),
	borderRadius: '100%',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	backgroundColor: token('color.icon.accent.blue', colors.B75),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	border: `1px solid ${token('color.border.accent.blue', colors.B100)}`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TitleComponent = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.text', colors.N600),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const PeriodDateComponent = styled.div({
	margin: 0,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.text', colors.N500),
	textTransform: 'uppercase',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const WeekBreakDownTitleComponent = styled.div(
	{
		height: '24px',
		display: 'flex',
		alignItems: 'center',
	},
	{
		margin: `0 0 ${token('space.200', '16px')} 0`,
	},
);
